import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  get,
  post,
  put
} from '../utils/api';

import {
  useAuth
} from '../context/AuthContext';

const REPORT_FIELDS = [
  [
    'executive_summary',
    'Executive Summary'
  ],
  [
    'audit_scope',
    'Audit Scope'
  ],
  [
    'transaction_review_summary',
    'Transaction Review Summary'
  ],
  [
    'physical_count_summary',
    'Physical Count Summary'
  ],
  [
    'variance_summary',
    'Variance Summary'
  ],
  [
    'internal_control_observations',
    'Internal Control Observations'
  ],
  [
    'recommendations',
    'Recommendations'
  ],
  [
    'overall_conclusion',
    'Overall Conclusion'
  ]
];

const SECTION_LABELS = {
  audit_day_1_scope:
    'Day 1 - Scope and Opening Review',
  audit_day_2_transactions:
    'Day 2 - Monthly Transaction Review',
  audit_day_3_physical_count:
    'Day 3 - Physical Stock Count',
  audit_day_4_reconciliation:
    'Day 4 - Reconciliation and Explanations',
  audit_day_5_close:
    'Day 5 - Final Review and Report'
};

const FINDING_CATEGORIES = [
  [
    'purchase_receipt',
    'Purchase Order Receipt'
  ],
  [
    'direct_receipt',
    'Direct Stock Receipt'
  ],
  [
    'material_issue',
    'Material Issue'
  ],
  [
    'production_return',
    'Production Return'
  ],
  [
    'material_conversion',
    'Material Conversion'
  ],
  [
    'stock_adjustment',
    'Stock Adjustment'
  ],
  [
    'parent_material',
    'Parent Material'
  ],
  [
    'child_sku',
    'Child SKU'
  ],
  [
    'finished_goods',
    'Finished Goods'
  ],
  [
    'damaged_or_rejected_stock',
    'Damaged or Rejected Stock'
  ],
  [
    'storage_and_identification',
    'Storage and Identification'
  ],
  [
    'record_integrity',
    'Record Integrity'
  ],
  [
    'internal_control',
    'Internal Control'
  ],
  [
    'other',
    'Other'
  ]
];

const FINDING_TYPES = [
  [
    'quantity_variance',
    'Quantity Variance'
  ],
  [
    'value_variance',
    'Value Variance'
  ],
  [
    'missing_document',
    'Missing Supporting Document'
  ],
  [
    'duplicate_record',
    'Duplicate Record'
  ],
  [
    'incorrect_classification',
    'Incorrect Classification'
  ],
  [
    'incorrect_cost',
    'Incorrect Cost'
  ],
  [
    'unrecorded_transaction',
    'Unrecorded Transaction'
  ],
  [
    'timing_difference',
    'Timing Difference'
  ],
  [
    'process_failure',
    'Process Failure'
  ],
  [
    'control_weakness',
    'Control Weakness'
  ],
  [
    'observation',
    'Observation'
  ]
];

const initialFinding = {
  category: 'parent_material',
  finding_type: 'observation',
  severity: 'medium',
  finding_title: '',
  finding_description: '',
  reference_type: '',
  reference_id: '',
  reference_number: '',
  item_name: '',
  system_quantity: '',
  physical_quantity: '',
  variance_quantity: '',
  system_value: '',
  verified_value: '',
  variance_value: '',
  auditor_recommendation: ''
};

const errorText = error =>
  error?.message ||
  'The requested action could not be completed.';

const monthKeyNow = () => {
  const parts = new Intl.DateTimeFormat(
    'en-CA',
    {
      timeZone: 'Africa/Lagos',
      year: 'numeric',
      month: '2-digit'
    }
  ).formatToParts(new Date());

  const year = parts.find(
    part => part.type === 'year'
  )?.value;

  const month = parts.find(
    part => part.type === 'month'
  )?.value;

  return `${year}-${month}`;
};

const formatDate = value => {
  if (!value) return 'Not recorded';

  return String(value).slice(0, 10);
};

const statusLabel = value =>
  String(value || 'draft')
    .replaceAll('_', ' ')
    .replace(
      /\b\w/g,
      character => character.toUpperCase()
    );

const panelStyle = {
  background: '#fff',
  border: '1px solid #e4ddd6',
  borderRadius: 12,
  padding: 18,
  marginBottom: 16
};

const noticeStyle = {
  background: '#F2EFE7',
  border: '1px solid #d6cabd',
  borderRadius: 10,
  padding: 14,
  marginBottom: 16,
  color: '#3B2A21'
};

function StatusBadge({
  status
}) {
  const colour = {
    draft: '#8a6116',
    returned: '#8F2E24',
    ready_for_submission: '#556B2F',
    submitted: '#235a7a',
    reviewed: '#556B2F',
    open: '#8F2E24',
    explained: '#8a6116',
    resolved: '#556B2F'
  }[status] || '#555';

  return (
    <span
      style={{
        display: 'inline-block',
        border: `1px solid ${colour}`,
        borderRadius: 999,
        color: colour,
        fontSize: 11,
        fontWeight: 800,
        padding: '4px 9px'
      }}
    >
      {statusLabel(status)}
    </span>
  );
}

function SchedulePanel({
  month,
  setMonth,
  schedule,
  loading,
  onLoad,
  onStart,
  canStart
}) {
  return (
    <div style={panelStyle}>
      <h3 style={{marginTop: 0}}>
        Monthly Audit Schedule
      </h3>

      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'end',
          flexWrap: 'wrap'
        }}
      >
        <label>
          <span
            style={{
              display: 'block',
              fontSize: 12,
              marginBottom: 5
            }}
          >
            Audit month
          </span>

          <input
            className="form-control"
            type="month"
            value={month}
            onChange={event =>
              setMonth(event.target.value)
            }
          />
        </label>

        <button
          className="btn btn-outline"
          type="button"
          disabled={loading || !month}
          onClick={onLoad}
        >
          View Schedule
        </button>

        {canStart && (
          <button
            className="btn btn-primary"
            type="button"
            disabled={loading || !month}
            onClick={onStart}
          >
            Start Monthly Reconciliation
          </button>
        )}
      </div>

      {schedule?.scheduled_dates?.length > 0 && (
        <div
          style={{
            marginTop: 15,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap'
          }}
        >
          {schedule.scheduled_dates.map(
            (date, index) => (
              <span
                key={date}
                style={{
                  background: '#F2EFE7',
                  borderRadius: 8,
                  padding: '7px 10px',
                  fontSize: 12,
                  fontWeight: 700
                }}
              >
                Day {index + 1}: {formatDate(date)}
              </span>
            )
          )}
        </div>
      )}

      <p
        className="text-muted text-sm"
        style={{marginBottom: 0}}
      >
        The schedule contains exactly five company
        working days. Weekends and registered public
        holidays are excluded.
      </p>
    </div>
  );
}

function ChecklistEditor({
  workspace,
  setWorkspace,
  saving,
  onSave,
  onComplete
}) {
  const grouped = useMemo(() => {
    const result = {};

    (
      workspace?.checklist_items || []
    ).forEach((item, index) => {
      const section = item.section || 'general';

      if (!result[section]) {
        result[section] = [];
      }

      result[section].push({
        item,
        index
      });
    });

    return result;
  }, [workspace]);

  const locked =
    Boolean(workspace?.checklist_complete) ||
    [
      'submitted',
      'reviewed'
    ].includes(workspace?.status);

  const updateItem = (
    index,
    changes
  ) => {
    setWorkspace(current => {
      const items = [
        ...(current.checklist_items || [])
      ];

      items[index] = {
        ...items[index],
        ...changes
      };

      return {
        ...current,
        checklist_items: items
      };
    });
  };

  return (
    <div style={panelStyle}>
      <div
        className="flex-between"
        style={{
          alignItems: 'flex-start',
          gap: 12
        }}
      >
        <div>
          <h3 style={{marginTop: 0}}>
            Five-Day Reconciliation Checklist
          </h3>

          <p className="text-muted text-sm">
            Complete the checklist using evidence from
            BloansBooks and the physical count sheets.
          </p>
        </div>

        <StatusBadge
          status={
            workspace?.checklist_complete
              ? 'reviewed'
              : 'draft'
          }
        />
      </div>

      {Object.entries(grouped).map(
        ([section, entries]) => (
          <div
            key={section}
            style={{
              borderTop: '1px solid #eee',
              paddingTop: 14,
              marginTop: 14
            }}
          >
            <h4>
              {SECTION_LABELS[section] ||
                statusLabel(section)}
            </h4>

            {entries.map(({
              item,
              index
            }) => (
              <div
                key={item.id || index}
                style={{
                  marginBottom: 12
                }}
              >
                {item.item_type ===
                'text_input' ? (
                  <label
                    style={{
                      display: 'block'
                    }}
                  >
                    <span
                      style={{
                        display: 'block',
                        fontWeight: 700,
                        marginBottom: 5
                      }}
                    >
                      {item.item_text}
                    </span>

                    <textarea
                      className="form-control"
                      rows="3"
                      disabled={locked}
                      value={item.value || ''}
                      onChange={event =>
                        updateItem(
                          index,
                          {
                            value:
                              event.target.value
                          }
                        )
                      }
                    />
                  </label>
                ) : (
                  <label
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start'
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={locked}
                      checked={Boolean(
                        item.checked
                      )}
                      onChange={event =>
                        updateItem(
                          index,
                          {
                            checked:
                              event.target.checked
                          }
                        )
                      }
                    />

                    <span>
                      {item.item_text}
                    </span>
                  </label>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {!locked && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            marginTop: 16
          }}
        >
          <button
            className="btn btn-outline"
            type="button"
            disabled={saving}
            onClick={onSave}
          >
            Save Checklist Progress
          </button>

          <button
            className="btn btn-primary"
            type="button"
            disabled={saving}
            onClick={onComplete}
          >
            Complete Five-Day Checklist
          </button>
        </div>
      )}
    </div>
  );
}

function FindingForm({
  finding,
  setFinding,
  saving,
  onSubmit
}) {
  const set = (
    field,
    value
  ) =>
    setFinding(current => ({
      ...current,
      [field]: value
    }));

  return (
    <div style={panelStyle}>
      <h3 style={{marginTop: 0}}>
        Record an Audit Finding
      </h3>

      <p className="text-muted text-sm">
        Findings do not change stock. Winner must explain
        each finding before the final report can be
        submitted.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit,minmax(210px,1fr))',
          gap: 12
        }}
      >
        <label>
          Category
          <select
            className="form-control"
            value={finding.category}
            onChange={event =>
              set(
                'category',
                event.target.value
              )
            }
          >
            {FINDING_CATEGORIES.map(
              ([value, label]) => (
                <option
                  key={value}
                  value={value}
                >
                  {label}
                </option>
              )
            )}
          </select>
        </label>

        <label>
          Finding type
          <select
            className="form-control"
            value={finding.finding_type}
            onChange={event =>
              set(
                'finding_type',
                event.target.value
              )
            }
          >
            {FINDING_TYPES.map(
              ([value, label]) => (
                <option
                  key={value}
                  value={value}
                >
                  {label}
                </option>
              )
            )}
          </select>
        </label>

        <label>
          Severity
          <select
            className="form-control"
            value={finding.severity}
            onChange={event =>
              set(
                'severity',
                event.target.value
              )
            }
          >
            <option value="low">
              Low
            </option>
            <option value="medium">
              Medium
            </option>
            <option value="high">
              High
            </option>
            <option value="critical">
              Critical
            </option>
          </select>
        </label>

        <label>
          Reference number
          <input
            className="form-control"
            value={finding.reference_number}
            onChange={event =>
              set(
                'reference_number',
                event.target.value
              )
            }
            placeholder="Receipt, PO, job or ledger reference"
          />
        </label>

        <label>
          Item or material
          <input
            className="form-control"
            value={finding.item_name}
            onChange={event =>
              set(
                'item_name',
                event.target.value
              )
            }
          />
        </label>

        <label>
          System quantity
          <input
            className="form-control"
            type="number"
            step="0.0001"
            value={finding.system_quantity}
            onChange={event =>
              set(
                'system_quantity',
                event.target.value
              )
            }
          />
        </label>

        <label>
          Physical quantity
          <input
            className="form-control"
            type="number"
            step="0.0001"
            value={finding.physical_quantity}
            onChange={event =>
              set(
                'physical_quantity',
                event.target.value
              )
            }
          />
        </label>

        <label>
          Quantity variance
          <input
            className="form-control"
            type="number"
            step="0.0001"
            value={finding.variance_quantity}
            onChange={event =>
              set(
                'variance_quantity',
                event.target.value
              )
            }
          />
        </label>

        <label>
          System value
          <input
            className="form-control"
            type="number"
            step="0.01"
            value={finding.system_value}
            onChange={event =>
              set(
                'system_value',
                event.target.value
              )
            }
          />
        </label>

        <label>
          Verified value
          <input
            className="form-control"
            type="number"
            step="0.01"
            value={finding.verified_value}
            onChange={event =>
              set(
                'verified_value',
                event.target.value
              )
            }
          />
        </label>

        <label>
          Value variance
          <input
            className="form-control"
            type="number"
            step="0.01"
            value={finding.variance_value}
            onChange={event =>
              set(
                'variance_value',
                event.target.value
              )
            }
          />
        </label>
      </div>

      <label
        style={{
          display: 'block',
          marginTop: 12
        }}
      >
        Finding title
        <input
          className="form-control"
          value={finding.finding_title}
          onChange={event =>
            set(
              'finding_title',
              event.target.value
            )
          }
        />
      </label>

      <label
        style={{
          display: 'block',
          marginTop: 12
        }}
      >
        Full description and evidence
        <textarea
          className="form-control"
          rows="4"
          value={finding.finding_description}
          onChange={event =>
            set(
              'finding_description',
              event.target.value
            )
          }
        />
      </label>

      <label
        style={{
          display: 'block',
          marginTop: 12
        }}
      >
        Auditor recommendation
        <textarea
          className="form-control"
          rows="3"
          value={finding.auditor_recommendation}
          onChange={event =>
            set(
              'auditor_recommendation',
              event.target.value
            )
          }
        />
      </label>

      <button
        className="btn btn-primary"
        type="button"
        disabled={
          saving ||
          !finding.finding_title.trim() ||
          !finding.finding_description.trim()
        }
        onClick={onSubmit}
        style={{marginTop: 14}}
      >
        Save Audit Finding
      </button>
    </div>
  );
}

function FindingsList({
  findings
}) {
  return (
    <div style={panelStyle}>
      <h3 style={{marginTop: 0}}>
        Recorded Findings
      </h3>

      {findings.length === 0 ? (
        <p className="text-muted">
          No findings have been recorded.
        </p>
      ) : (
        findings.map(finding => (
          <div
            key={finding.id}
            style={{
              borderTop: '1px solid #eee',
              padding: '14px 0'
            }}
          >
            <div
              className="flex-between"
              style={{
                alignItems: 'flex-start',
                gap: 12
              }}
            >
              <div>
                <strong>
                  {finding.finding_title}
                </strong>

                <div
                  className="text-muted text-sm"
                  style={{marginTop: 4}}
                >
                  {statusLabel(
                    finding.category
                  )}
                  {' | '}
                  {statusLabel(
                    finding.finding_type
                  )}
                  {' | '}
                  {statusLabel(
                    finding.severity
                  )}
                </div>
              </div>

              <StatusBadge
                status={finding.status}
              />
            </div>

            <p>{finding.finding_description}</p>

            {finding.reference_number && (
              <p className="text-sm">
                <strong>Reference:</strong>{' '}
                {finding.reference_number}
              </p>
            )}

            {finding.auditor_recommendation && (
              <p className="text-sm">
                <strong>
                  Recommended action:
                </strong>{' '}
                {finding.auditor_recommendation}
              </p>
            )}

            {finding.inventory_officer_explanation && (
              <div style={noticeStyle}>
                <strong>
                  Inventory Officer explanation
                </strong>

                <div style={{marginTop: 6}}>
                  {
                    finding
                      .inventory_officer_explanation
                  }
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function ReportEditor({
  workspace,
  setWorkspace,
  saving,
  onSave,
  onSubmit
}) {
  const locked = [
    'submitted',
    'reviewed'
  ].includes(workspace.status);

  return (
    <div style={panelStyle}>
      <div
        className="flex-between"
        style={{
          alignItems: 'flex-start',
          gap: 12
        }}
      >
        <div>
          <h3 style={{marginTop: 0}}>
            Final Reconciliation Report
          </h3>

          <p className="text-muted text-sm">
            Save drafts throughout the five days. Submit
            only after the checklist is complete and every
            finding has an Inventory Officer explanation.
          </p>
        </div>

        <StatusBadge
          status={workspace.status}
        />
      </div>

      {workspace.review_notes && (
        <div style={noticeStyle}>
          <strong>
            Management review notes
          </strong>

          <div style={{marginTop: 6}}>
            {workspace.review_notes}
          </div>
        </div>
      )}

      {REPORT_FIELDS.map(
        ([field, label]) => (
          <label
            key={field}
            style={{
              display: 'block',
              marginBottom: 14
            }}
          >
            <span
              style={{
                display: 'block',
                fontWeight: 700,
                marginBottom: 5
              }}
            >
              {label}
            </span>

            <textarea
              className="form-control"
              rows="4"
              disabled={locked}
              value={workspace[field] || ''}
              onChange={event =>
                setWorkspace(current => ({
                  ...current,
                  [field]:
                    event.target.value
                }))
              }
            />
          </label>
        )
      )}

      {!locked && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap'
          }}
        >
          <button
            className="btn btn-outline"
            type="button"
            disabled={saving}
            onClick={onSave}
          >
            Save Report Draft
          </button>

          <button
            className="btn btn-primary"
            type="button"
            disabled={saving}
            onClick={onSubmit}
          >
            Submit Final Reconciliation
          </button>
        </div>
      )}
    </div>
  );
}

function AuditorWorkspace() {
  const [month, setMonth] =
    useState(monthKeyNow());

  const [schedule, setSchedule] =
    useState(null);

  const [workspace, setWorkspace] =
    useState(null);

  const [finding, setFinding] =
    useState(initialFinding);

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [message, setMessage] =
    useState('');

  const [error, setError] =
    useState('');

  const loadSchedule = useCallback(
    async () => {
      if (!month) return;

      const [year, monthNumber] =
        month.split('-');

      setLoading(true);
      setError('');

      try {
        const response = await get(
          `/inventory-auditor/schedule/${year}/${monthNumber}`
        );

        setSchedule(response.data || null);
      } catch (requestError) {
        setError(errorText(requestError));
      } finally {
        setLoading(false);
      }
    },
    [month]
  );

  const loadWorkspace = useCallback(
    async () => {
      setLoading(true);
      setError('');

      try {
        const response = await get(
          '/inventory-auditor/reconciliations/current'
        );

        setWorkspace(response.data || null);

        if (response.data?.period_month) {
          setMonth(
            String(
              response.data.period_month
            ).slice(0, 7)
          );
        }
      } catch (requestError) {
        setError(errorText(requestError));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const start = async () => {
    setSaving(true);
    setError('');
    setMessage('');

    try {
      const response = await post(
        '/inventory-auditor/reconciliations/start',
        {
          month
        }
      );

      setWorkspace(response.data || null);
      setMessage(
        response.message ||
        'Monthly reconciliation started.'
      );
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  const saveChecklist = async () => {
    setSaving(true);
    setError('');
    setMessage('');

    try {
      const response = await put(
        `/checklists/${workspace.checklist_id}/items`,
        {
          items:
            workspace.checklist_items,
          summary_data:
            workspace.summary_data || {}
        }
      );

      setWorkspace(current => ({
        ...current,
        checklist_items:
          response.data?.items ||
          current.checklist_items,
        summary_data:
          response.data?.summary_data ||
          current.summary_data
      }));

      setMessage(
        'Checklist progress saved.'
      );
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  const completeChecklist = async () => {
    if (
      !window.confirm(
        'Complete this five-day checklist? It cannot be reopened through this screen.'
      )
    ) {
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      await put(
        `/checklists/${workspace.checklist_id}/complete`,
        {
          items:
            workspace.checklist_items,
          summary_data:
            workspace.summary_data || {}
        }
      );

      await loadWorkspace();

      setMessage(
        'Five-day checklist completed.'
      );
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  const saveReport = async () => {
    setSaving(true);
    setError('');
    setMessage('');

    try {
      const body = {};

      REPORT_FIELDS.forEach(
        ([field]) => {
          body[field] =
            workspace[field] || '';
        }
      );

      const response = await put(
        `/inventory-auditor/reconciliations/${workspace.id}/report`,
        body
      );

      setWorkspace(current => ({
        ...current,
        ...(response.data || {})
      }));

      setMessage(
        'Report draft saved.'
      );
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  const addFinding = async () => {
    setSaving(true);
    setError('');
    setMessage('');

    try {
      await post(
        `/inventory-auditor/reconciliations/${workspace.id}/findings`,
        finding
      );

      setFinding(initialFinding);
      await loadWorkspace();

      setMessage(
        'Audit finding recorded.'
      );
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  const submitReport = async () => {
    if (
      !window.confirm(
        'Submit the final reconciliation report to Super Admin? Finding explanations will be locked after submission.'
      )
    ) {
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      await post(
        `/inventory-auditor/reconciliations/${workspace.id}/submit`,
        {}
      );

      await loadWorkspace();

      setMessage(
        'Final reconciliation report submitted.'
      );
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SchedulePanel
        month={month}
        setMonth={setMonth}
        schedule={schedule}
        loading={loading || saving}
        onLoad={loadSchedule}
        onStart={start}
        canStart={!workspace}
      />

      {!workspace && !loading && (
        <div style={noticeStyle}>
          No reconciliation has been started. It may be
          started only on one of the scheduled audit days.
        </div>
      )}

      {workspace && (
        <>
          <div style={noticeStyle}>
            <strong>
              Audit period:
            </strong>{' '}
            {String(
              workspace.period_month
            ).slice(0, 7)}
            {' | '}
            <strong>
              Scheduled dates:
            </strong>{' '}
            {
              (
                workspace.schedule_dates ||
                schedule?.scheduled_dates ||
                []
              )
                .map(formatDate)
                .join(', ')
            }
          </div>

          <AuditorSnapshotPanel workspace={workspace} />

          <ChecklistEditor
            workspace={workspace}
            setWorkspace={setWorkspace}
            saving={saving}
            onSave={saveChecklist}
            onComplete={completeChecklist}
          />

          {![
            'submitted',
            'reviewed'
          ].includes(workspace.status) && (
            <FindingForm
              finding={finding}
              setFinding={setFinding}
              saving={saving}
              onSubmit={addFinding}
            />
          )}

          <FindingsList
            findings={
              workspace.findings || []
            }
          />

          <ReportEditor
            workspace={workspace}
            setWorkspace={setWorkspace}
            saving={saving}
            onSave={saveReport}
            onSubmit={submitReport}
          />
        </>
      )}

      {message && (
        <div
          className="alert alert-success"
          style={{marginTop: 12}}
        >
          {message}
        </div>
      )}

      {error && (
        <div
          className="alert alert-danger"
          style={{marginTop: 12}}
        >
          {error}
        </div>
      )}
    </>
  );
}

const previousAuditMonth = month => {
  const [year, number] = month.split('-').map(Number);
  const previous = new Date(Date.UTC(year, number - 2, 1));
  return `${previous.getUTCFullYear()}-${String(
    previous.getUTCMonth() + 1
  ).padStart(2, '0')}`;
};

const lagosTimestamp = value => value
  ? new Intl.DateTimeFormat('en-NG', {
      timeZone: 'Africa/Lagos',
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(value))
  : 'Not recorded';

function SavedSnapshotLines({ data }) {
  if (!data) return null;

  return (
    <details style={{ marginTop: 12 }}>
      <summary>
        View saved 2:00 pm quantities ({data.lines.length} items)
      </summary>
      <div style={{ maxHeight: 420, overflow: 'auto', marginTop: 10 }}>
        <table className="table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Type</th>
              <th>Code / Item</th>
              <th>Quantity at 2:00 pm</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map(line => (
              <tr key={`${line.item_type}:${line.item_id}`}>
                <td>{statusLabel(line.item_type)}</td>
                <td>
                  {line.item_code || line.item_id}
                  <br />
                  {line.item_name || 'Name no longer available'}
                </td>
                <td>
                  {line.system_quantity}{' '}
                  {line.unit_of_measure || ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function InventorySnapshotPanel() {
  const [month, setMonth] = useState(monthKeyNow());
  const [snapshot, setSnapshot] = useState(null);
  const [lines, setLines] = useState(null);
  const [events, setEvents] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    if (!/^\d{4}-\d{2}$/.test(month)) return;

    const [year, number] = month.split('-');
    setLoading(true);
    setError('');

    try {
      const response = await get(
        `/inventory-auditor/snapshots/${year}/${number}`
      );
      const saved = response.data || null;
      setSnapshot(saved);

      if (saved) {
        const [lineResponse, eventResponse] = await Promise.all([
          get(`/inventory-auditor/snapshots/${year}/${number}/lines`),
          get(`/inventory-auditor/post-snapshot/${year}/${number}`)
        ]);
        setLines(lineResponse.data || null);
        setEvents(eventResponse.data?.events || []);
      } else {
        setLines(null);
        setEvents([]);
      }
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const capture = async () => {
    if (!window.confirm(
      `Capture the saved 2:00 pm stock position for ${month}? ` +
      'This can be done only once.'
    )) return;

    const [year, number] = month.split('-');
    setSaving(true);
    setError('');
    setMessage('');

    try {
      await post(
        `/inventory-auditor/snapshots/${year}/${number}/capture`,
        {}
      );
      await load();
      setMessage(
        'The 2:00 pm stock position was saved. ' +
        'Normal Inventory work may continue.'
      );
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  const setDraft = (id, field, value) => setDrafts(current => ({
    ...current,
    [id]: { ...current[id], [field]: value }
  }));

  const saveNote = async event => {
    const draft = drafts[event.id] || {};
    const reference = String(
      draft.reference_number ?? event.reference_number ?? ''
    ).trim();
    const businessDate =
      draft.business_date ?? event.business_date ?? event.posting_date;
    const explanation = String(
      draft.explanation ?? event.explanation ?? ''
    ).trim();

    if (!reference || !businessDate || !explanation) {
      setError(
        'Enter the reference, actual business date and explanation.'
      );
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      await put(
        `/inventory-auditor/post-snapshot/${event.id}/note`,
        {
          reference_number: reference,
          business_date: businessDate,
          explanation
        }
      );
      await load();
      setMessage(
        'Movement details recorded. The posting date was not changed.'
      );
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={panelStyle}>
      <h3 style={{ marginTop: 0 }}>
        2:00 pm Stock Snapshot and Later Movements
      </h3>
      <p className="text-muted text-sm">
        The cutoff is for the audit snapshot. Stock transactions
        continue and keep their true dates. Later movements are
        documented here for the following monthly audit.
      </p>

      <label>
        Audit month{' '}
        <input
          className="form-control"
          type="month"
          value={month}
          onChange={event => setMonth(event.target.value)}
        />
      </label>{' '}
      <button
        className="btn btn-outline"
        type="button"
        disabled={loading || saving}
        onClick={load}
      >
        Refresh
      </button>

      {loading && <div className="spinner" />}

      {!loading && !snapshot && (
        <div style={noticeStyle}>
          No saved 2:00 pm snapshot for this month. Capture becomes
          available after the final company working day's cutoff.
        </div>
      )}

      {!loading && !snapshot && (
        <button
          className="btn btn-primary"
          type="button"
          disabled={saving || !month}
          onClick={capture}
        >
          Capture 2:00 pm Snapshot
        </button>
      )}

      {snapshot && (
        <div style={noticeStyle}>
          Cutoff: {lagosTimestamp(snapshot.target_cutoff_at)}.
          {' '}Captured: {lagosTimestamp(snapshot.captured_at)}.
          {' '}Saved items: {snapshot.item_count}.
          {' '}This does not freeze Inventory.
        </div>
      )}

      <SavedSnapshotLines data={lines} />

      {snapshot && (
        <h4>Movements after the snapshot ({events.length})</h4>
      )}
      {snapshot && events.length === 0 && (
        <p>No later balance changes are recorded for this month.</p>
      )}

      {events.map(event => (
        <div
          key={event.id}
          style={{ borderTop: '1px solid #ddd', padding: '12px 0' }}
        >
          <strong>
            {event.item_code || event.item_id}
            {' — '}
            {event.item_name || statusLabel(event.item_type)}
          </strong>
          <div className="text-muted text-sm">
            Posted {lagosTimestamp(event.posted_at)}
            {' | '}
            {event.quantity_before} → {event.quantity_after}
            {' | Change '}
            {event.quantity_change}
          </div>

          {event.reviewed_at ? (
            <p>
              Reviewed in the following audit: {event.review_notes}
            </p>
          ) : (
            <>
              <label>
                Transaction reference
                <input
                  className="form-control"
                  value={
                    drafts[event.id]?.reference_number ??
                    event.reference_number ?? ''
                  }
                  onChange={e => setDraft(
                    event.id, 'reference_number', e.target.value
                  )}
                />
              </label>
              <label>
                Actual business date
                <input
                  className="form-control"
                  type="date"
                  value={
                    drafts[event.id]?.business_date ??
                    event.business_date ??
                    event.posting_date
                  }
                  onChange={e => setDraft(
                    event.id, 'business_date', e.target.value
                  )}
                />
              </label>
              <label>
                Movement and stock effect
                <textarea
                  className="form-control"
                  rows="3"
                  value={
                    drafts[event.id]?.explanation ??
                    event.explanation ?? ''
                  }
                  onChange={e => setDraft(
                    event.id, 'explanation', e.target.value
                  )}
                />
              </label>
              <button
                className="btn btn-primary"
                type="button"
                disabled={saving}
                onClick={() => saveNote(event)}
              >
                Save Movement Details
              </button>
            </>
          )}
        </div>
      ))}

      {snapshot && <OtherPostSnapshotOfficer month={month} />}
      {message && (
        <div className="alert alert-success">{message}</div>
      )}
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
    </div>
  );
}

function AuditorSnapshotPanel({ workspace }) {
  const month = String(workspace.period_month).slice(0, 7);
  const prior = previousAuditMonth(month);
  const [current, setCurrent] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [events, setEvents] = useState([]);
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    const [year, number] = month.split('-');
    const [priorYear, priorNumber] = prior.split('-');
    setError('');

    try {
      const [now, earlier] = await Promise.all([
        get(`/inventory-auditor/snapshots/${year}/${number}`),
        get(`/inventory-auditor/snapshots/${priorYear}/${priorNumber}`)
      ]);

      setPrevious(earlier.data || null);

      if (now.data) {
        const result = await get(
          `/inventory-auditor/snapshots/${year}/${number}/lines`
        );
        setCurrent(result.data || null);
      } else {
        setCurrent(null);
      }

      if (earlier.data) {
        const result = await get(
          `/inventory-auditor/post-snapshot/${priorYear}/${priorNumber}`
        );
        setEvents(result.data?.events || []);
      } else {
        setEvents([]);
      }
    } catch (requestError) {
      setError(errorText(requestError));
    }
  }, [month, prior]);

  useEffect(() => { load(); }, [load]);

  const review = async event => {
    const text = String(notes[event.id] || '').trim();
    if (text.length < 20) {
      setError(
        'Record at least 20 characters describing the ledger ' +
        'and opening-balance check.'
      );
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      await put(
        `/inventory-auditor/post-snapshot/${event.id}/review`,
        {
          reconciliation_id: workspace.id,
          review_notes: text
        }
      );
      await load();
      setMessage('Following-month movement review saved.');
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={panelStyle}>
      <h3 style={{ marginTop: 0 }}>
        2:00 pm Snapshot and Following-Month Review
      </h3>
      <p className="text-muted text-sm">
        The current report covers stock through its saved 2:00 pm
        cutoff. Later transactions keep their actual dates and are
        reviewed in the following month. Do not describe the snapshot
        as full calendar-month coverage.
      </p>

      {current ? (
        <>
          <div style={noticeStyle}>
            Current audit cutoff:
            {' '}
            {lagosTimestamp(current.snapshot.target_cutoff_at)}.
            {' '}Saved items: {current.snapshot.item_count}.
          </div>
          <SavedSnapshotLines data={current} />
        </>
      ) : (
        <div style={noticeStyle}>
          The current month's 2:00 pm snapshot has not been
          captured. Final submission will be blocked.
        </div>
      )}

      <h4>Review {prior} movements after its cutoff</h4>
      {!previous && (
        <p>
          No prior snapshot is available. If the balance journal
          covered that month, final submission will be blocked.
        </p>
      )}
      {previous && events.length === 0 && (
        <p>
          No post-snapshot balance changes were recorded in {prior}.
        </p>
      )}

      {events.map(event => (
        <div
          key={event.id}
          style={{ borderTop: '1px solid #ddd', padding: '12px 0' }}
        >
          <strong>
            {event.item_code || event.item_id}
            {' — '}
            {event.item_name || statusLabel(event.item_type)}
          </strong>
          <p>
            Posted {lagosTimestamp(event.posted_at)}
            {' | '}
            {event.quantity_before} → {event.quantity_after}.
            {' '}Reference: {event.reference_number || 'Missing'};
            {' '}business date: {event.business_date || 'Missing'}.
          </p>
          <p>
            {event.explanation ||
              'The Inventory Officer has not documented this movement.'}
          </p>

          {event.reviewed_at ? (
            <p>Reviewed: {event.review_notes}</p>
          ) : event.reference_number &&
            event.explanation &&
            !['submitted', 'reviewed'].includes(workspace.status) && (
              <>
                <label>
                  Verification against original-period ledger
                  and next opening balance
                  <textarea
                    className="form-control"
                    rows="3"
                    value={notes[event.id] || ''}
                    onChange={e => setNotes(currentNotes => ({
                      ...currentNotes,
                      [event.id]: e.target.value
                    }))}
                  />
                </label>
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={saving}
                  onClick={() => review(event)}
                >
                  Record Following-Month Review
                </button>
              </>
            )}
        </div>
      ))}

      <button
        className="btn btn-outline"
        type="button"
        onClick={load}
      >
        Refresh Snapshot and Register
      </button>
      <OtherPostSnapshotAuditor month={prior} workspace={workspace} />
      {message && (
        <div className="alert alert-success">{message}</div>
      )}
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
    </div>
  );
}


const otherPostingTypes = [
  ['inventory_ledger', 'Inventory ledger'],
  ['inventory_event', 'Inventory event'],
  ['inventory_transaction', 'Inventory transaction'],
  ['stock_count', 'Stock count'],
  ['receipt', 'Receipt'],
  ['issue', 'Issue'],
  ['production_record', 'Production record'],
  ['cost_correction', 'Cost correction'],
  ['other', 'Other posting']
];

function OtherPostSnapshotOfficer({ month }) {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({
    source_type: 'inventory_transaction',
    reference_number: '',
    posted_at: '',
    business_date: '',
    quantity_change: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    const [year, number] = month.split('-');
    try {
      const result = await get(
        `/inventory-auditor/post-snapshot-other/${year}/${number}`
      );
      setEntries(result.data || []);
    } catch (requestError) {
      setError(errorText(requestError));
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const update = (field, value) =>
    setForm(current => ({ ...current, [field]: value }));

  const save = async () => {
    if (!form.reference_number.trim() || !form.business_date ||
        !form.description.trim() ||
        !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(form.posted_at)) {
      setError(
        'Enter a reference, actual Lagos posting time, business date and description.'
      );
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await post('/inventory-auditor/post-snapshot-other', {
        period_month: month,
        source_type: form.source_type,
        reference_number: form.reference_number.trim(),
        posted_at: `${form.posted_at}:00+01:00`,
        business_date: form.business_date,
        quantity_change: form.quantity_change,
        description: form.description.trim()
      });
      setForm(current => ({
        ...current,
        reference_number: '',
        posted_at: '',
        business_date: '',
        quantity_change: '',
        description: ''
      }));
      await load();
      setMessage('The later posting was recorded with its actual date and time.');
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{ borderTop: '1px solid #ddd', marginTop: 20, paddingTop: 16 }}>
      <h4>Other Post-Snapshot Postings</h4>
      <p>
        Enter later postings that the automatic stock-balance journal does
        not capture. Check the source record and automatic register first
        to avoid duplicates. Keep the real posting and business dates.
      </p>
      <label>Source type
        <select className="form-control" value={form.source_type}
          onChange={e => update('source_type', e.target.value)}>
          {otherPostingTypes.map(([value, label]) =>
            <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label>Transaction reference
        <input className="form-control" value={form.reference_number}
          onChange={e => update('reference_number', e.target.value)} />
      </label>
      <label>Actual posting time (Africa/Lagos)
        <input className="form-control" type="datetime-local"
          value={form.posted_at}
          onChange={e => update('posted_at', e.target.value)} />
      </label>
      <label>Actual business date
        <input className="form-control" type="date"
          value={form.business_date}
          onChange={e => update('business_date', e.target.value)} />
      </label>
      <label>Quantity effect, if any
        <input className="form-control" type="number" step="any"
          value={form.quantity_change}
          onChange={e => update('quantity_change', e.target.value)} />
      </label>
      <label>Item, transaction and stock effect
        <textarea className="form-control" rows="3"
          value={form.description}
          onChange={e => update('description', e.target.value)} />
      </label>
      <button className="btn btn-primary" type="button"
        disabled={saving} onClick={save}>Record Later Posting</button>
      <h4>Recorded other postings ({entries.length})</h4>
      {entries.length === 0 &&
        <p>No other postings are recorded for this month.</p>}
      {entries.map(entry => (
        <div key={entry.id}
          style={{ borderTop: '1px solid #ddd', padding: '10px 0' }}>
          <strong>{entry.reference_number}</strong> — {entry.source_type}
          <div>Posted {lagosTimestamp(entry.posted_at)};
            business date {entry.business_date_text}</div>
          <div>{entry.description}</div>
          {entry.reviewed_at && <div>Reviewed: {entry.review_notes}</div>}
        </div>
      ))}
      <button className="btn btn-outline" type="button"
        onClick={load}>Refresh Other Posting Register</button>
      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}
    </section>
  );
}

function OtherPostSnapshotAuditor({ month, workspace }) {
  const [entries, setEntries] = useState([]);
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    const [year, number] = month.split('-');
    try {
      const result = await get(
        `/inventory-auditor/post-snapshot-other/${year}/${number}`
      );
      setEntries(result.data || []);
    } catch (requestError) {
      setError(errorText(requestError));
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const review = async entry => {
    const reviewNotes = String(notes[entry.id] || '').trim();
    if (reviewNotes.length < 20) {
      setError('Describe the source-record and opening-balance check in at least 20 characters.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await put(`/inventory-auditor/post-snapshot-other/${entry.id}/review`, {
        reconciliation_id: workspace.id,
        review_notes: reviewNotes
      });
      await load();
      setMessage('Following-month review saved.');
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{ borderTop: '1px solid #ddd', marginTop: 20, paddingTop: 16 }}>
      <h4>Review Other Post-Snapshot Postings from {month}</h4>
      <p>
        Compare this manually entered register with the original-period
        source records and the following month's opening balances. Verify
        completeness against the source extract.
      </p>
      {entries.length === 0 &&
        <p>No other postings are recorded for that month.</p>}
      {entries.map(entry => (
        <div key={entry.id}
          style={{ borderTop: '1px solid #ddd', padding: '12px 0' }}>
          <strong>{entry.reference_number}</strong> — {entry.source_type}
          <div>Posted {lagosTimestamp(entry.posted_at)};
            business date {entry.business_date_text}</div>
          <p>{entry.description}</p>
          {entry.reviewed_at ? <p>Reviewed: {entry.review_notes}</p> :
            !['submitted', 'reviewed'].includes(workspace.status) && <>
              <label>Source-record and opening-balance review
                <textarea className="form-control" rows="3"
                  value={notes[entry.id] || ''}
                  onChange={e => setNotes(current => ({
                    ...current, [entry.id]: e.target.value
                  }))} />
              </label>
              <button className="btn btn-primary" type="button"
                disabled={saving} onClick={() => review(entry)}>
                Record Following-Month Review
              </button>
            </>}
        </div>
      ))}
      <button className="btn btn-outline" type="button"
        onClick={load}>Refresh Other Posting Review</button>
      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}
    </section>
  );
}

function InventoryFindingQueue() {
  const [findings, setFindings] =
    useState([]);

  const [explanations, setExplanations] =
    useState({});

  const [loading, setLoading] =
    useState(true);

  const [savingId, setSavingId] =
    useState('');

  const [message, setMessage] =
    useState('');

  const [error, setError] =
    useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await get(
        '/inventory-auditor/findings/pending-explanation'
      );

      setFindings(response.data || []);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const explain = async finding => {
    const explanation =
      String(
        explanations[finding.id] || ''
      ).trim();

    if (!explanation) return;

    setSavingId(finding.id);
    setError('');
    setMessage('');

    try {
      await put(
        `/inventory-auditor/findings/${finding.id}/explanation`,
        {
          explanation
        }
      );

      setExplanations(current => ({
        ...current,
        [finding.id]: ''
      }));

      await load();

      setMessage(
        'Inventory explanation recorded.'
      );
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSavingId('');
    }
  };

  return (
    <div style={panelStyle}>
      <h3 style={{marginTop: 0}}>
        Inventory Findings Requiring Explanation
      </h3>

      <p className="text-muted text-sm">
        Explain the record or physical-count discrepancy.
        This screen cannot change stock, cost or ledger
        transactions.
      </p>

      {loading ? (
        <div className="spinner" />
      ) : findings.length === 0 ? (
        <div style={noticeStyle}>
          No audit findings currently require an
          explanation.
        </div>
      ) : (
        findings.map(finding => (
          <div
            key={finding.id}
            style={{
              borderTop: '1px solid #eee',
              padding: '16px 0'
            }}
          >
            <div
              className="flex-between"
              style={{
                alignItems: 'flex-start',
                gap: 12
              }}
            >
              <div>
                <strong>
                  {finding.finding_title}
                </strong>

                <div className="text-muted text-sm">
                  {statusLabel(
                    finding.category
                  )}
                  {' | '}
                  {statusLabel(
                    finding.severity
                  )}
                  {finding.reference_number
                    ? ` | ${finding.reference_number}`
                    : ''}
                </div>
              </div>

              <StatusBadge
                status={finding.status}
              />
            </div>

            <p>{finding.finding_description}</p>

            <label
              style={{
                display: 'block'
              }}
            >
              Inventory Officer explanation
              <textarea
                className="form-control"
                rows="4"
                value={
                  explanations[finding.id] ??
                  finding
                    .inventory_officer_explanation ??
                  ''
                }
                onChange={event =>
                  setExplanations(current => ({
                    ...current,
                    [finding.id]:
                      event.target.value
                  }))
                }
              />
            </label>

            <button
              className="btn btn-primary"
              type="button"
              style={{marginTop: 10}}
              disabled={
                savingId === finding.id ||
                !String(
                  explanations[finding.id] ??
                  finding
                    .inventory_officer_explanation ??
                  ''
                ).trim()
              }
              onClick={() =>
                explain(finding)
              }
            >
              Save Explanation
            </button>
          </div>
        ))
      )}

      {message && (
        <div className="alert alert-success">
          {message}
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}
    </div>
  );
}

function ManagementRegister() {
  const [records, setRecords] =
    useState([]);

  const [notes, setNotes] =
    useState({});

  const [loading, setLoading] =
    useState(true);

  const [savingId, setSavingId] =
    useState('');

  const [message, setMessage] =
    useState('');

  const [error, setError] =
    useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await get(
        '/inventory-auditor/reconciliations'
      );

      setRecords(response.data || []);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (
    record,
    decision
  ) => {
    const reviewNotes =
      String(notes[record.id] || '').trim();

    if (
      decision === 'returned' &&
      !reviewNotes
    ) {
      setError(
        'Review notes are required when returning a report.'
      );

      return;
    }

    if (
      !window.confirm(
        decision === 'reviewed'
          ? 'Approve this submitted Inventory reconciliation report?'
          : 'Return this report to the Inventory Auditor for correction?'
      )
    ) {
      return;
    }

    setSavingId(record.id);
    setError('');
    setMessage('');

    try {
      await put(
        `/inventory-auditor/reconciliations/${record.id}/review`,
        {
          decision,
          review_notes: reviewNotes
        }
      );

      await load();

      setMessage(
        decision === 'reviewed'
          ? 'Inventory audit report approved.'
          : 'Inventory audit report returned for correction.'
      );
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSavingId('');
    }
  };

  return (
    <div style={panelStyle}>
      <h3 style={{marginTop: 0}}>
        Inventory Reconciliation Report Register
      </h3>

      <p className="text-muted text-sm">
        Super Admin may approve or return only reports that
        have been formally submitted.
      </p>

      {loading ? (
        <div className="spinner" />
      ) : records.length === 0 ? (
        <div style={noticeStyle}>
          No monthly reconciliation report exists yet.
        </div>
      ) : (
        records.map(record => (
          <div
            key={record.id}
            style={{
              borderTop: '1px solid #eee',
              padding: '16px 0'
            }}
          >
            <div
              className="flex-between"
              style={{
                alignItems: 'flex-start',
                gap: 12
              }}
            >
              <div>
                <strong>
                  {record.auditor_name ||
                    record.user_name ||
                    'Inventory Auditor'}
                </strong>

                <div className="text-muted text-sm">
                  Period:{' '}
                  {String(
                    record.period_month
                  ).slice(0, 7)}
                  {' | '}
                  Findings:{' '}
                  {record.total_findings || 0}
                  {' | '}
                  Open:{' '}
                  {record.open_findings || 0}
                </div>
              </div>

              <StatusBadge
                status={record.status}
              />
            </div>

            {REPORT_FIELDS.map(
              ([field, label]) =>
                record[field] ? (
                  <div
                    key={field}
                    style={{marginTop: 10}}
                  >
                    <strong>{label}</strong>
                    <div
                      style={{
                        whiteSpace: 'pre-wrap',
                        marginTop: 3
                      }}
                    >
                      {record[field]}
                    </div>
                  </div>
                ) : null
            )}

            {record.status === 'submitted' && (
              <>
                <label
                  style={{
                    display: 'block',
                    marginTop: 14
                  }}
                >
                  Management review notes
                  <textarea
                    className="form-control"
                    rows="3"
                    value={notes[record.id] || ''}
                    onChange={event =>
                      setNotes(current => ({
                        ...current,
                        [record.id]:
                          event.target.value
                      }))
                    }
                  />
                </label>

                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    flexWrap: 'wrap',
                    marginTop: 10
                  }}
                >
                  <button
                    className="btn btn-primary"
                    type="button"
                    disabled={
                      savingId === record.id
                    }
                    onClick={() =>
                      review(
                        record,
                        'reviewed'
                      )
                    }
                  >
                    Approve Report
                  </button>

                  <button
                    className="btn btn-outline"
                    type="button"
                    disabled={
                      savingId === record.id
                    }
                    onClick={() =>
                      review(
                        record,
                        'returned'
                      )
                    }
                  >
                    Return for Correction
                  </button>
                </div>
              </>
            )}

            {record.review_notes && (
              <div
                style={{
                  ...noticeStyle,
                  marginTop: 12
                }}
              >
                <strong>
                  Review notes
                </strong>

                <div style={{marginTop: 5}}>
                  {record.review_notes}
                </div>
              </div>
            )}
          </div>
        ))
      )}

      {message && (
        <div className="alert alert-success">
          {message}
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}
    </div>
  );
}

export default function InventoryAuditorWorkspace() {
  const {
    user
  } = useAuth();

  const role = user?.role;

  const allowed = [
    'inventory_auditor',
    'inventory_officer',
    'super_admin',
    'admin'
  ].includes(role);

  if (!allowed) {
    return (
      <div className="alert alert-danger">
        You do not have access to the Inventory Audit
        Workspace.
      </div>
    );
  }

  return (
    <div>
      <div
        className="flex-between mb-lg"
        style={{
          alignItems: 'flex-start',
          gap: 12
        }}
      >
        <div>
          <h2 className="page-title">
            Inventory Audit Workspace
          </h2>

          <p className="text-muted text-sm">
            Monthly stock reconciliation, controlled
            findings, explanations and final management
            review.
          </p>
        </div>
      </div>

      {role === 'inventory_auditor' && (
        <AuditorWorkspace />
      )}

      {[
        'inventory_officer',
        'super_admin',
        'admin'
      ].includes(role) && (
        <InventoryFindingQueue />
      )}

      {['inventory_officer', 'super_admin'].includes(role) && (
        <InventorySnapshotPanel />
      )}

      {role === 'super_admin' && (
        <ManagementRegister />
      )}
    </div>
  );
}
